import React from 'react';

import { Box, Grid } from '@sunwisesoftware/sunwise-ui';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'common/components';
import {
    ReactHookFormIntlNumberInput,
    ReactHookFormMultiSelect,
    ReactHookFormSelect,
} from 'common/components/form/bootstrap';
import {
    beamSpacingOptions,
    getFieldsToRender,
    getStrategyOptions,
    lFootSupportsWidthOptions,
    plateSupportLengthOptions,
    railSpacingOptions,
    railSupportLengthOptions,
    supportLengthOptions,
} from 'common/utils/helpers/systemsValues';

import resource_hbd from 'resources/hbd.png';
import resource_larp from 'resources/larp.png';
import resource_lee from 'resources/lee.png';
import resource_re from 'resources/re.png';

const RenderAdditionalInputsPerSystem = ({
    control,
    disabled,
    setValue,
    systemUid,
}) => {
    const fieldsToRender = getFieldsToRender(systemUid);
    const { t } = useTranslation();
    const strategyOptions = getStrategyOptions();

    const [
        beam_spacing,
        front_base_height,
        lfoot_supports_width,
        outer_rail_length,
        plate_support_length,
        rail_spacing,
        rail_support_length,
        support_length,
    ] = useWatch({
        control,
        name: [
            'beam_spacing',
            'front_base_height',
            'lfoot_supports_width',
            'outer_rail_length',
            'plate_support_length',
            'rail_spacing',
            'rail_support_length',
            'support_length',
        ],
    });

    /* -------------------------------------------------------------------------- */
    /*  Todos los inputs ahora reciben la prop "disabled"                        */
    /* -------------------------------------------------------------------------- */

    const rail_spacing_field = fieldsToRender.includes('rail_spacing') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Rail length')}
                            name="rail_spacing.strategy"
                            onChange={(e) => {
                                switch (e.target.value) {
                                    case 'manual':
                                        setValue('rail_spacing', {
                                            strategy: 'manual',
                                            value: [],
                                        });
                                        break;
                                    case 'custom':
                                        setValue('rail_spacing', {
                                            strategy: 'custom',
                                            custom_value: '',
                                        });
                                        break;
                                    default:
                                        setValue('rail_spacing', {
                                            strategy: e.target.value,
                                        });
                                }
                            }}
                            options={[
                                ...strategyOptions,
                                { label: i18next.t('Custom'), value: 'custom' },
                            ]}
                        />
                    </Grid>

                    {rail_spacing?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormMultiSelect
                                allTextValue={t('All text', { count: 2 })}
                                control={control}
                                disabled={disabled}
                                label={i18next.t('Rail length')}
                                name="rail_spacing.value"
                                options={railSpacingOptions() || []}
                            />
                        </Grid>
                    )}

                    {rail_spacing?.strategy === 'custom' && (
                        <Grid size="grow">
                            <ReactHookFormIntlNumberInput
                                append="mm"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={i18next.t('Rail length')}
                                name="rail_spacing.custom_value"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_re} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const rail_support_length_field = fieldsToRender.includes(
        'rail_support_length',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Rail support length')}
                            name="rail_support_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {rail_support_length?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormSelect
                                control={control}
                                disabled={disabled}
                                label={i18next.t('Rail support length')}
                                name="rail_support_length.value"
                                options={railSupportLengthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_larp} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const plate_support_length_field = fieldsToRender.includes(
        'plate_support_length',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Plate support length')}
                            name="plate_support_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {plate_support_length?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormSelect
                                control={control}
                                disabled={disabled}
                                label={i18next.t('Plate support length')}
                                name="plate_support_length.value"
                                options={plateSupportLengthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_larp} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const beam_spacing_field = fieldsToRender.includes('beam_spacing') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Beam spacing')}
                            name="beam_spacing.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {beam_spacing?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormSelect
                                control={control}
                                disabled={disabled}
                                label={i18next.t('Beam spacing')}
                                name="beam_spacing.value"
                                options={beamSpacingOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_lee} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const support_length_field = fieldsToRender.includes('support_length') && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={
                                systemUid === 'ARCK-LOSA CONVENCIONAL'
                                    ? t('Column spacing')
                                    : i18next.t('Support length')
                            }
                            name="support_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {support_length?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormSelect
                                control={control}
                                disabled={disabled}
                                label={
                                    systemUid === 'ARCK-LOSA CONVENCIONAL'
                                        ? t('Column spacing')
                                        : i18next.t('Support length')
                                }
                                name="support_length.value"
                                options={supportLengthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_lee} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const lfoot_supports_width_field = fieldsToRender.includes(
        'lfoot_supports_width',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Lfoot supports width')}
                            name="lfoot_supports_width.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {lfoot_supports_width?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormSelect
                                control={control}
                                disabled={disabled}
                                label={i18next.t('Lfoot supports width')}
                                name="lfoot_supports_width.value"
                                options={lFootSupportsWidthOptions}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_larp} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const outer_rail_length_field = fieldsToRender.includes(
        'outer_rail_length',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Outer rail length')}
                            name="outer_rail_length.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {outer_rail_length?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormIntlNumberInput
                                append="mm"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={i18next.t('Outer rail length')}
                                name="outer_rail_length.value"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_re} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const front_base_height_field = fieldsToRender.includes(
        'front_base_height',
    ) && (
        <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    <Grid size="grow">
                        <ReactHookFormSelect
                            control={control}
                            disabled={disabled}
                            label={i18next.t('Front base height')}
                            name="front_base_height.strategy"
                            options={strategyOptions}
                        />
                    </Grid>

                    {front_base_height?.strategy === 'manual' && (
                        <Grid size="grow">
                            <ReactHookFormIntlNumberInput
                                append="mm"
                                control={control}
                                disabled={disabled}
                                fullWidth
                                label={i18next.t('Front base height')}
                                name="front_base_height.value"
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box>
                <Tooltip sx={{ maxWidth: '400px' }}>
                    <img src={resource_hbd} alt="" />
                </Tooltip>
            </Box>
        </Box>
    );

    const showsAnyAdditionalSettings =
        rail_spacing_field ||
        rail_support_length_field ||
        plate_support_length_field ||
        beam_spacing_field ||
        support_length_field ||
        lfoot_supports_width_field ||
        outer_rail_length_field ||
        front_base_height_field;

    if (showsAnyAdditionalSettings)
        return (
            <>
                <>{rail_spacing_field}</>
                <>{rail_support_length_field}</>
                <>{plate_support_length_field}</>
                <>{beam_spacing_field}</>
                <>{support_length_field}</>
                <>{lfoot_supports_width_field}</>
                <>{outer_rail_length_field}</>
                <>{front_base_height_field}</>
            </>
        );

    return null;
};

RenderAdditionalInputsPerSystem.propTypes = {
    control: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    setValue: PropTypes.func.isRequired,
    systemUid: PropTypes.string.isRequired,
};

export default RenderAdditionalInputsPerSystem;
